/**
 * Image Compression Utility for EXRSIM
 * 
 * Provides consistent image sizing and compression across the application.
 * All images are compressed before being stored as base64 in the database.
 */

// Image size configurations by type
export const IMAGE_CONFIG = {
  // Profile images / Avatars (200x200 to 400x400)
  profile: {
    maxWidth: 400,
    maxHeight: 400,
    quality: 0.8,
    maxSizeKB: 150,
    format: 'image/jpeg'
  },
  // Resource/Equipment images (800x600)
  resource: {
    maxWidth: 800,
    maxHeight: 600,
    quality: 0.75,
    maxSizeKB: 300,
    format: 'image/jpeg'
  },
  // HIRA Hazard images (1200x800)
  hazard: {
    maxWidth: 1200,
    maxHeight: 800,
    quality: 0.75,
    maxSizeKB: 500,
    format: 'image/jpeg'
  },
  // Scenario images (1920x1080)
  scenario: {
    maxWidth: 1920,
    maxHeight: 1080,
    quality: 0.7,
    maxSizeKB: 800,
    format: 'image/jpeg'
  },
  // Exercise images (1200x800)
  exercise: {
    maxWidth: 1200,
    maxHeight: 800,
    quality: 0.75,
    maxSizeKB: 500,
    format: 'image/jpeg'
  },
  // Thumbnail for list views (100x100)
  thumbnail: {
    maxWidth: 100,
    maxHeight: 100,
    quality: 0.7,
    maxSizeKB: 20,
    format: 'image/jpeg'
  },
  // Generic/default (800x600)
  default: {
    maxWidth: 800,
    maxHeight: 600,
    quality: 0.75,
    maxSizeKB: 300,
    format: 'image/jpeg'
  }
};

/**
 * Compress and resize an image file
 * @param {File} file - The image file to compress
 * @param {string} type - The image type ('profile', 'resource', 'hazard', 'scenario', 'exercise', 'thumbnail', 'default')
 * @returns {Promise<string>} - Base64 encoded compressed image
 */
export const compressImage = (file, type = 'default') => {
  return new Promise((resolve, reject) => {
    const config = IMAGE_CONFIG[type] || IMAGE_CONFIG.default;
    
    // Create file reader
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        const { maxWidth, maxHeight } = config;
        
        // Calculate scaling factor
        const widthRatio = maxWidth / width;
        const heightRatio = maxHeight / height;
        const ratio = Math.min(widthRatio, heightRatio, 1); // Don't upscale
        
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
        
        // Create canvas for resizing
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        
        // Use better image smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Draw resized image
        ctx.drawImage(img, 0, 0, width, height);
        
        // Compress with quality adjustment to meet size target
        let quality = config.quality;
        let result = canvas.toDataURL(config.format, quality);
        
        // Iteratively reduce quality if image is still too large
        const maxSizeBytes = config.maxSizeKB * 1024;
        let attempts = 0;
        const maxAttempts = 10;
        
        while (getBase64Size(result) > maxSizeBytes && quality > 0.1 && attempts < maxAttempts) {
          quality -= 0.1;
          result = canvas.toDataURL(config.format, quality);
          attempts++;
        }
        
        // Log compression results for debugging
        const originalSize = file.size;
        const compressedSize = getBase64Size(result);
        console.log(`Image compressed: ${(originalSize / 1024).toFixed(1)}KB → ${(compressedSize / 1024).toFixed(1)}KB (${width}x${height}, quality: ${quality.toFixed(1)})`);
        
        resolve(result);
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      
      img.src = e.target.result;
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Get the size of a base64 string in bytes
 * @param {string} base64String - The base64 encoded string
 * @returns {number} - Size in bytes
 */
export const getBase64Size = (base64String) => {
  // Remove data URL prefix if present
  const base64 = base64String.split(',')[1] || base64String;
  // Calculate size: base64 uses 4 chars per 3 bytes, with padding
  const padding = (base64.match(/=/g) || []).length;
  return Math.floor((base64.length * 3) / 4) - padding;
};

/**
 * Format file size for display
 * @param {number} bytes - Size in bytes
 * @returns {string} - Formatted size string
 */
export const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Validate image file before processing
 * @param {File} file - The file to validate
 * @returns {Object} - { valid: boolean, error: string|null }
 */
export const validateImageFile = (file) => {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
  const maxOriginalSize = 10 * 1024 * 1024; // 10MB max original file size
  
  if (!file) {
    return { valid: false, error: 'No file selected' };
  }
  
  if (!validTypes.includes(file.type.toLowerCase())) {
    return { valid: false, error: 'Invalid file type. Please use JPEG, PNG, GIF, or WebP.' };
  }
  
  if (file.size > maxOriginalSize) {
    return { valid: false, error: `File too large. Maximum size is ${formatFileSize(maxOriginalSize)}.` };
  }
  
  return { valid: true, error: null };
};

/**
 * Process image file with validation and compression
 * @param {File} file - The image file to process
 * @param {string} type - The image type for compression settings
 * @returns {Promise<{success: boolean, data: string|null, error: string|null}>}
 */
export const processImageFile = async (file, type = 'default') => {
  // Validate file
  const validation = validateImageFile(file);
  if (!validation.valid) {
    return { success: false, data: null, error: validation.error };
  }
  
  try {
    const compressedImage = await compressImage(file, type);
    return { success: true, data: compressedImage, error: null };
  } catch (error) {
    console.error('Image processing error:', error);
    return { success: false, data: null, error: 'Failed to process image. Please try another file.' };
  }
};

export default {
  IMAGE_CONFIG,
  compressImage,
  getBase64Size,
  formatFileSize,
  validateImageFile,
  processImageFile
};
